// src/pages/Admin/StudentsAdmin.jsx
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../supabaseClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const SEGMENTS = { DELETED: 'deleted', ACTIVE: 'active', ALL: 'all' };

// 🔧 Set this to your actual Supabase Storage bucket for student documents
const BUCKET_NAME = 'student-docs';

/**
 * Helper: remove all Supabase Storage files for a student's documents.
 * Uses table `student_documents(storage_path)` and a single known bucket.
 * Returns { removedFiles } count.
 */
async function deleteStudentFiles(client, studentId) {
  const { data: docs, error: docErr } = await client
    .from('student_documents')
    .select('storage_path')
    .eq('student_id', studentId);

  if (docErr) throw docErr;
  if (!docs || !docs.length) return { removedFiles: 0 };

  const paths = docs
    .map((d) => d.storage_path)
    .filter(Boolean);

  if (!paths.length) return { removedFiles: 0 };

  // Remove in chunks to avoid overly large payloads
  const chunk = (arr, size) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

  let removedFiles = 0;
  for (const part of chunk(paths, 100)) {
    const { data, error } = await client.storage.from(BUCKET_NAME).remove(part);
    if (error) {
      // Fail fast to avoid deleting DB rows while files remain
      throw error;
    }
    removedFiles += part.length;
  }

  return { removedFiles };
}

export default function StudentsAdmin() {
  const [segment, setSegment] = useState(SEGMENTS.DELETED);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [working, setWorking] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerStudentId, setDrawerStudentId] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerData, setDrawerData] = useState(null);

  // --- Fetch students (joined children for counts + total)
  const { data: students = [], isLoading, refetch } = useQuery({
    queryKey: ['students_admin', segment, searchTerm],
    queryFn: async () => {
      let query = supabase.from('students').select(`
        id, full_name, email, nric_passport, contact_no, address, enrollment_year,
        payment_type, status, is_deleted, created_at, updated_at, date_of_birth,
        enrollments ( id, is_deleted, status ),
        payments ( id, is_deleted, amount, status, invoice_number, payment_method, payment_type, payment_date )
      `);

      if (segment === SEGMENTS.ACTIVE) query = query.eq('is_deleted', false);
      if (segment === SEGMENTS.DELETED) query = query.eq('is_deleted', true);

      if (searchTerm) {
        query = query.or(
          `full_name.ilike.%${searchTerm}%,nric_passport.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
        );
      }

      // Default sort: full_name ASC
      query = query.order('full_name', { ascending: true });

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // --- KPI counts
  const { data: kpis = { active: 0, deleted: 0 } } = useQuery({
    queryKey: ['students_admin_kpis'],
    queryFn: async () => {
      const { count: activeCount } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', false);
      const { count: deletedCount } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', true);
      return { active: activeCount || 0, deleted: deletedCount || 0 };
    },
    staleTime: 60_000,
  });

  // --- Helpers
  const currency = (n) => `RM${Number(n || 0).toFixed(2)}`;
  const totalPaidFor = (payments) =>
    (payments || [])
      .filter((p) => !p.is_deleted) // all statuses, non-deleted only
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  // --- Derived rows for table
  const rows = useMemo(
    () =>
      students.map((s) => {
        const enrs = s.enrollments || [];
        const pays = s.payments || [];
        const showAll = segment === 'deleted'; // <- only in Deleted tab, count everything
        return {
          ...s,
          enrollmentCount: showAll ? enrs.length : enrs.filter((e) => !e.is_deleted).length,
          paymentCount: showAll ? pays.length : pays.filter((p) => !p.is_deleted).length,
          totalPaid: pays
            .filter((p) => !p.is_deleted) // keep this as “active only” if that’s your KPI definition
            .reduce((sum, p) => sum + Number(p.amount || 0), 0),
        };
      }),
    [students, segment]
  );

  // --- Selection
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // --- CSV (no external deps)
  const csvHeaders = [
    'Full Name',
    'Email',
    'NRIC/Passport',
    'Enrollments (#)',
    'Payments (#)',
    'Total Paid (RM)',
    'Deleted?',
  ];
  const toCSV = (arr) => {
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = arr
      .map((r) =>
        [
          r.full_name,
          r.email,
          r.nric_passport,
          r.enrollmentCount,
          r.paymentCount,
          r.totalPaid.toFixed(2),
          r.is_deleted ? 'Yes' : 'No',
        ]
          .map(escape)
          .join(',')
      )
      .join('\n');
    return [csvHeaders.join(','), body].join('\n');
  };
  const downloadCSV = () => {
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students_admin_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Drawer data loader
  const openDetails = useCallback(async (id) => {
    setDrawerStudentId(id);
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .select(`
          id, full_name, email, nric_passport, contact_no, address, enrollment_year,
          payment_type, status, is_deleted, created_at, updated_at, date_of_birth, remarks,
          enrollments ( id, status, is_deleted, enrollment_date, subject_id, intake_id ),
          payments ( id, invoice_number, amount, status, payment_method, payment_type, payment_date, is_deleted )
        `)
        .eq('id', id)
        .single();
      if (error) throw error;
      setDrawerData(data);
    } catch (e) {
      console.error(e);
      setDrawerData(null);
      alert('Failed to load student details.');
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const closeDetails = () => {
    setDrawerOpen(false);
    setDrawerStudentId(null);
    setDrawerData(null);
  };

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') closeDetails();
    };
    if (drawerOpen) window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [drawerOpen]);

  // --- Actions (unchanged functionality)
  const restoreOne = async (id) => {
    setWorking(true);
    try {
      let { error } = await supabase.from('students').update({ is_deleted: false }).eq('id', id);
      if (error) throw error;

      const [{ error: e1 }, { error: e2 }, { error: e3 }] = await Promise.all([
        supabase.from('enrollments').update({ is_deleted: false }).eq('student_id', id),
        supabase.from('payments').update({ is_deleted: false }).eq('student_id', id),
        supabase.from('student_documents').update({ is_deleted: false }).eq('student_id', id), // optional
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      if (e3) throw e3;

      await refetch();
    } catch (e) {
      console.error(e);
      alert('Failed to restore student.');
    } finally {
      setWorking(false);
    }
  };

  const purgeOne = async (id, name) => {
    const ok = window.confirm(
      `Type OK in the next prompt to confirm HARD DELETE of "${name}".\n` +
      `This will remove Storage files, exam registrations, document rows, payments/enrollments, then the student.`
    );
    if (!ok) return;
    const typed = window.prompt('Confirm by typing OK (case-sensitive):', '');
    if (typed !== 'OK') return;

    setWorking(true);
    try {
      // 1) Remove Storage files first (fail fast if it errors)
      const { removedFiles } = await deleteStudentFiles(supabase, id);

      // 2) Call DB RPC for atomic row cleanup
      const { data, error } = await supabase.rpc('purge_student', { p_student_id: id });
      if (error) throw error;
      const r = Array.isArray(data) && data[0] ? data[0] : null;

      if (r) {
        alert(
          `Purged "${name}".\n` +
          `storage_files_removed: ${removedFiles}\n` +
          `exam_info: ${r.removed_exam_info ?? 0}\n` +
          `exam_sessions: ${r.removed_exam_sessions ?? 0}\n` +
          `student_documents_rows: ${r.removed_student_documents ?? 0}\n` +
          `payment_other_charges: ${r.removed_payment_other_charges}\n` +
          `payment_enrollments: ${r.removed_payment_enrollments}\n` +
          `payments: ${r.removed_payments}\n` +
          `enrollments: ${r.removed_enrollments}\n` +
          `students: ${r.removed_students}`
        );
      } else {
        alert(`Purged "${name}".\nstorage_files_removed: ${removedFiles}`);
      }

      await refetch();
      setSelected((prev) => {
        const copy = new Set(prev);
        copy.delete(id);
        return copy;
      });
      if (drawerStudentId === id) closeDetails();
    } catch (e) {
      console.error(e);
      alert('Hard delete failed. See console for details.');
    } finally {
      setWorking(false);
    }
  };

  const restoreBulk = async () => {
    if (selected.size === 0) return;
    setWorking(true);
    try {
      const ids = Array.from(selected);

      // students
      let { error } = await supabase.from('students').update({ is_deleted: false }).in('id', ids);
      if (error) throw error;

      // children
      const [{ error: e1 }, { error: e2 }, { error: e3 }] = await Promise.all([
        supabase.from('enrollments').update({ is_deleted: false }).in('student_id', ids),
        supabase.from('payments').update({ is_deleted: false }).in('student_id', ids),
        supabase.from('student_documents').update({ is_deleted: false }).in('student_id', ids), // optional
      ]);
      if (e1) throw e1;
      if (e2) throw e2;
      if (e3) throw e3;

      await refetch();
      clearSelection();
    } catch (e) {
      console.error(e);
      alert('Bulk restore failed.');
    } finally {
      setWorking(false);
    }
  };

  const purgeBulk = async () => {
    if (selected.size === 0) return;
    const ok = window.confirm(
      `You are about to HARD DELETE ${selected.size} student(s).\n` +
      `This will remove Storage files, exam registrations, document rows, payments/enrollments, then the students.\n` +
      `This action is PERMANENT. Continue?`
    );
    if (!ok) return;

    setWorking(true);
    try {
      const ids = Array.from(selected);
      let totals = {
        removed_files: 0,
        removed_exam_info: 0,
        removed_exam_sessions: 0,
        removed_student_documents: 0,
        removed_payment_other_charges: 0,
        removed_payment_enrollments: 0,
        removed_payments: 0,
        removed_enrollments: 0,
        removed_students: 0,
      };

      for (const id of ids) {
        // 1) Remove Storage files for this student
        const { removedFiles } = await deleteStudentFiles(supabase, id);
        totals.removed_files += removedFiles;

        // 2) DB cleanup
        const { data, error } = await supabase.rpc('purge_student', { p_student_id: id });
        if (error) throw error;
        const r = Array.isArray(data) && data[0] ? data[0] : null;
        if (r) {
          totals.removed_exam_info += Number(r.removed_exam_info || 0);
          totals.removed_exam_sessions += Number(r.removed_exam_sessions || 0);
          totals.removed_student_documents += Number(r.removed_student_documents || 0);
          totals.removed_payment_other_charges += Number(r.removed_payment_other_charges || 0);
          totals.removed_payment_enrollments += Number(r.removed_payment_enrollments || 0);
          totals.removed_payments += Number(r.removed_payments || 0);
          totals.removed_enrollments += Number(r.removed_enrollments || 0);
          totals.removed_students += Number(r.removed_students || 0);
        }
      }
      await refetch();
      clearSelection();
      alert(
        `Bulk purge completed.\n` +
        `storage_files_removed: ${totals.removed_files}\n` +
        `exam_info: ${totals.removed_exam_info}\n` +
        `exam_sessions: ${totals.removed_exam_sessions}\n` +
        `student_documents_rows: ${totals.removed_student_documents}\n` +
        `payment_other_charges: ${totals.removed_payment_other_charges}\n` +
        `payment_enrollments: ${totals.removed_payment_enrollments}\n` +
        `payments: ${totals.removed_payments}\n` +
        `enrollments: ${totals.removed_enrollments}\n` +
        `students: ${totals.removed_students}`
      );
    } catch (e) {
      console.error(e);
      alert('Bulk hard delete failed.');
    } finally {
      setWorking(false);
    }
  };

  const isDeletedView = segment === SEGMENTS.DELETED;

  return (
    <div className="space-y-6">
      {/* Header (match Payments Admin tone) */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Students Master Administration</h1>
            <p className="text-sm text-gray-500 mt-1">
              Review, restore, permanently delete, or export students.
            </p>
          </div>
          <div className="flex items-center gap-2">
{/*            <Button variant="outline" onClick={() => refetch()} disabled={working}>
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button onClick={downloadCSV} disabled={working}>
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              Export CSV
            </Button>
*/}
            <Button
                variant="ghost"
                className="flex items-center gap-2"
                onClick={downloadCSV}
                disabled={working || (rows?.length ?? 0) === 0}
                title={rows.length ? 'Export current rows to CSV' : 'Nothing to export'}
            >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards (consistent with Payments Admin look) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{kpis.active}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-emerald-500 opacity-80" />
          </div>
        </Card>
        <Card>
          <div className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Deleted</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{kpis.deleted}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-rose-500 opacity-80" />
          </div>
        </Card>
      </div>

      {/* Middle controls (segment buttons + search aligned like Payments Admin) */}
      <Card>
        <div className="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Segmented buttons (Deleted / Active / All) */}
          <div className="inline-flex rounded-md shadow-sm overflow-hidden border border-gray-200">
            {[
              { key: SEGMENTS.DELETED, label: 'Deleted' },
              { key: SEGMENTS.ACTIVE, label: 'Active' },
              { key: SEGMENTS.ALL, label: 'All' },
            ].map((seg, idx) => (
              <button
                key={seg.key}
                onClick={() => {
                  setSegment(seg.key);
                  clearSelection();
                  refetch();
                }}
                className={[
                  'px-4 py-2 text-sm font-medium focus:outline-none transition',
                  segment === seg.key
                    ? 'bg-gray-800 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                  idx !== 0 ? 'border-l border-gray-200' : '',
                ].join(' ')}
              >
                {seg.label}
              </button>
            ))}
          </div>
{/* Search field (Payments Admin style)
   
          <div className="relative w-full md:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search student name / NRIC / email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div> */}
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search student name / NRIC / email"
                className="ml-3 flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300"
            />

        </div>
      </Card>

      {/* Bulk Toolbar (same placement/feel) */}
      {selected.size > 0 && (
        <Card>
          <div className="flex justify-between items-center p-4">
            <span className="text-sm">
              <b>{selected.size}</b> selected
            </span>
            <div className="flex gap-2">
              {isDeletedView ? (
                <>
                  <Button onClick={restoreBulk} disabled={working}>Restore</Button>
                  <Button variant="danger" onClick={purgeBulk} disabled={working}>
                    Hard Delete
                  </Button>
                </>
              ) : (
                <span className="text-sm text-gray-500">
                  Bulk actions are available in the <b>Deleted</b> segment.
                </span>
              )}
              <Button variant="outline" onClick={clearSelection} disabled={working}>
                Clear
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Results Table (spacing/typography aligned) */}
      <Card>
        {isLoading ? (
          <div className="text-center py-10 text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No students found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10" />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    NRIC/Passport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enrollments
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payments
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Paid (RM)
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deleted?
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4">
                      <input
                        type="checkbox"
                        checked={selected.has(s.id)}
                        onChange={() => toggleSelect(s.id)}
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {s.full_name}
                      <div className="text-xs text-gray-500">{s.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{s.nric_passport}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{s.email}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">{s.enrollmentCount}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">{s.paymentCount}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">{s.totalPaid.toFixed(2)}</td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          s.is_deleted ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {s.is_deleted ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => openDetails(s.id)}>
                          Details
                        </Button>
                        {s.is_deleted ? (
                          <>
                            <Button variant="outline" onClick={() => restoreOne(s.id)} disabled={working}>
                              Restore
                            </Button>
                            <Button variant="danger" onClick={() => purgeOne(s.id, s.full_name)} disabled={working}>
                              Hard Delete
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 self-center">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Details Drawer (unchanged functionality; spacing aligned) */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 transition-opacity z-40"
            onClick={closeDetails}
          />
          <div
            className="fixed inset-y-0 right-0 w-full sm:w-[560px] bg-white shadow-xl z-50 flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {drawerData?.full_name || 'Student Details'}
              </h2>
              <button onClick={closeDetails} className="p-2 rounded hover:bg-gray-100">
                <XMarkIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {drawerLoading ? (
                <div className="py-12 text-center text-gray-500">Loading…</div>
              ) : !drawerData ? (
                <div className="py-12 text-center text-gray-500">No data.</div>
              ) : (
                <div className="space-y-6">
                  {/* Summary */}
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-gray-500">Full Name</div>
                        <div className="text-gray-900">{drawerData.full_name}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">NRIC/Passport</div>
                        <div className="text-gray-900">{drawerData.nric_passport}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Email</div>
                        <div className="text-gray-900">{drawerData.email}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Contact No.</div>
                        <div className="text-gray-900">{drawerData.contact_no || '-'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Enrollment Year</div>
                        <div className="text-gray-900">{drawerData.enrollment_year}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Enrollment Type</div>
                        <div className="text-gray-900">{drawerData.payment_type}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Status</div>
                        <div className="text-gray-900">{drawerData.status}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Date of Birth</div>
                        <div className="text-gray-900">
                          {drawerData.date_of_birth
                            ? new Date(drawerData.date_of_birth).toLocaleDateString()
                            : '-'}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-gray-500">Address</div>
                        <div className="text-gray-900">{drawerData.address || '-'}</div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-gray-500">Remarks</div>
                        <div className="text-gray-900 whitespace-pre-wrap">
                          {drawerData.remarks || '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Created</div>
                        <div className="text-gray-900">
                          {drawerData.created_at
                            ? new Date(drawerData.created_at).toLocaleString()
                            : '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Updated</div>
                        <div className="text-gray-900">
                          {drawerData.updated_at
                            ? new Date(drawerData.updated_at).toLocaleString()
                            : '-'}
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Enrollments */}
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Enrollments</h3>
                    {drawerData.enrollments?.length ? (
                      <div className="overflow-x-auto border rounded">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-500">ID</th>
                              <th className="px-3 py-2 text-left text-gray-500">Status</th>
                              <th className="px-3 py-2 text-left text-gray-500">Enrolled</th>
                              <th className="px-3 py-2 text-left text-gray-500">Subject ID</th>
                              <th className="px-3 py-2 text-left text-gray-500">Intake ID</th>
                              <th className="px-3 py-2 text-right text-gray-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {drawerData.enrollments
                              .slice()
                              .sort((a, b) => (a.enrollment_date || '').localeCompare(b.enrollment_date || ''))
                              .map((e) => (
                                <tr key={e.id}>
                                  <td className="px-3 py-2">{e.id}</td>
                                  <td className="px-3 py-2">
                                    <span
                                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        e.status === 'Completed'
                                          ? 'bg-green-100 text-green-800'
                                          : e.status === 'Active'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : e.status === 'Withdrawn'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-gray-100 text-gray-800'
                                      }`}
                                    >
                                      {e.status}
                                    </span>
                                    {e.is_deleted && (
                                      <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-rose-100 text-rose-800">
                                        Deleted
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {e.enrollment_date
                                      ? new Date(e.enrollment_date).toLocaleDateString()
                                      : '-'}
                                  </td>
                                  <td className="px-3 py-2">{e.subject_id || '-'}</td>
                                  <td className="px-3 py-2">{e.intake_id || '-'}</td>
                                  <td className="px-3 py-2 text-right">
                                    <a
                                      href={`#/enrollments/${e.id}/edit`}
                                      className="text-indigo-600 hover:text-indigo-800"
                                    >
                                      Edit
                                    </a>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">No enrollments.</div>
                    )}
                  </section>

                  {/* Payments */}
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Payments</h3>
                    {drawerData.payments?.length ? (
                      <>
                        <div className="mb-2 text-sm text-gray-700">
                          <span className="font-medium">Total Paid (non-deleted, any status):</span>{' '}
                          {currency(
                            drawerData.payments
                              .filter((p) => !p.is_deleted)
                              .reduce((sum, p) => sum + Number(p.amount || 0), 0)
                          )}
                        </div>
                        <div className="overflow-x-auto border rounded">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-gray-500">Invoice</th>
                                <th className="px-3 py-2 text-left text-gray-500">Amount</th>
                                <th className="px-3 py-2 text-left text-gray-500">Date</th>
                                <th className="px-3 py-2 text-left text-gray-500">Method</th>
                                <th className="px-3 py-2 text-left text-gray-500">Type</th>
                                <th className="px-3 py-2 text-left text-gray-500">Status</th>
                                <th className="px-3 py-2 text-right text-gray-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {drawerData.payments
                                .slice()
                                .sort((a, b) => (a.payment_date || '').localeCompare(b.payment_date || ''))
                                .map((p) => (
                                  <tr key={p.id}>
                                    <td className="px-3 py-2">{p.invoice_number}</td>
                                    <td className="px-3 py-2">{currency(p.amount)}</td>
                                    <td className="px-3 py-2">
                                      {p.payment_date
                                        ? new Date(p.payment_date).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                          })
                                        : '-'}
                                    </td>
                                    <td className="px-3 py-2">{p.payment_method || '-'}</td>
                                    <td className="px-3 py-2">
                                      <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          p.payment_type === 'Full'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-indigo-100 text-indigo-800'
                                        }`}
                                      >
                                        {p.payment_type || '-'}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2">
                                      <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                          p.status === 'Paid'
                                            ? 'bg-green-100 text-green-800'
                                            : p.status === 'Pending'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : p.status === 'Refunded'
                                            ? 'bg-rose-100 text-rose-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }`}
                                      >
                                        {p.status}
                                      </span>
                                      {p.is_deleted && (
                                        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-rose-100 text-rose-800">
                                          Deleted
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <a
                                        href={`#/payments/${p.id}`}
                                        className="text-blue-600 hover:text-blue-800 mr-3"
                                      >
                                        View
                                      </a>
                                      <a
                                        href={`#/payments/${p.id}/edit?fromStudent=${drawerStudentId}`}
                                        className="text-indigo-600 hover:text-indigo-800"
                                      >
                                        Edit
                                      </a>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-500 text-sm">No payments.</div>
                    )}
                  </section>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t flex justify-end gap-2">
              {drawerData?.is_deleted ? (
                <>
                  <Button variant="outline" onClick={() => restoreOne(drawerStudentId)} disabled={working}>
                    Restore
                  </Button>
                  <Button variant="danger" onClick={() => purgeOne(drawerStudentId, drawerData.full_name)} disabled={working}>
                    Hard Delete
                  </Button>
                </>
              ) : (
                <div className="text-xs text-gray-500 self-center">—</div>
              )}
              <Button variant="outline" onClick={closeDetails}>
                Close
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
